import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import ChevronRight from 'common/Icons/ChevronRight';
import { AI_CI_PERFORMANCE, AICI_COMMUNITY_FIELD } from 'components/AiCompetitiveIntelligence/constants';
import { categoryLabel } from 'components/AiCompetitiveIntelligence/helpers';
import GradientBorderButton from 'components/lib/GradientBorderButton';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { CompanyAICompetitiveIntelligenceRow } from 'types/recommendations';

import { selectNotableCommunities } from './notableCommunitiesLadder';
import NotableCommunitiesTable from './NotableCommunitiesTable';

const Container = styled.div`
  border-radius: 10px;
  padding: 30px;
  margin-bottom: 30px;
  background-image: linear-gradient(
    73deg,
    var(--market-position-gradient-start),
    var(--market-position-gradient-mid) 57%,
    var(--market-position-gradient-end) 100%
  );
  color: var(--white);
`;

const Header = styled.div`
  display: flex;
  flex-flow: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
  align-items: center;
`;

const ImageContainer = styled.div`
  background-image: linear-gradient(
    315deg,
    var(--competitor-purple),
    var(--aici-action-icon-gradient-mid) 50%,
    var(--flamingo-pink) 0%
  );
  padding: 6px;
  border-radius: 4px;
  width: 20px;
  height: 20px;
`;

const Title = styled.div`
  font-size: 20px;
  font-family: var(--font-bold);
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: var(--white-60);
`;

const Actions = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
`;

const AICIButton = styled(GradientBorderButton)`
  border: none;
  border-radius: 4px;
  box-shadow: none;
  flex-shrink: 0;

  &::before {
    display: none;
  }

  .gradient-border-button-content {
    font-family: var(--font-bold);
    font-weight: 700;
    font-size: 14px;
    gap: 8px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-flow: row;
  gap: 30px;
  margin-top: 20px;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 60px 0;
`;

const LeftColumn = styled.div`
  width: 325px;
  flex-shrink: 0;
`;

const StatsBox = styled.div`
  display: flex;
  flex-flow: row;
  background-color: var(--white-10);
  border-radius: 10px;
  padding: 15px 20px;
`;

const StatBlock = styled.div`
  flex: 1;
  min-width: 0;
  cursor: pointer;
  padding: 6px 10px;
  margin: -6px -10px;
  border-radius: 8px;
  transition: background-color 0.15s;

  &:hover {
    background-color: var(--white-15);
  }
`;

const StatDivider = styled.div`
  align-self: stretch;
  width: 1px;
  margin: 0 15px;
  background-color: var(--white-15);
`;

const StatBigNumber = styled.div`
  font-size: 40px;
`;

const StatUnit = styled.div`
  font-size: 14px;
`;

const StatStatus = styled.div`
  font-size: 14px;

  &.leading {
    color: var(--positive-green);
  }
  &.lagging {
    color: var(--inactive-orange);
  }
`;

const PatternsLabel = styled.div`
  font-size: 11px;
  letter-spacing: 1px;
  color: var(--white-60);
  margin-top: 30px;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--white-15);
`;

const PatternsEmptyMessage = styled.div`
  font-size: 13px;
  color: var(--white-60);
`;

const PatternsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PatternRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  cursor: pointer;
  padding: 6px 10px;
  margin: -6px -10px;
  border-radius: 8px;
  transition: background-color 0.15s;

  &:hover {
    background-color: var(--white-10);
  }
`;

const PatternCategory = styled.span`
  font-size: 14px;
  font-family: var(--font-bold);
`;

const PatternDetail = styled.span`
  font-size: 13px;
  text-align: right;

  &.leading {
    color: var(--positive-green);
  }
  &.lagging {
    color: var(--inactive-orange);
  }
`;

const RightColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

type Props = {
  rows: CompanyAICompetitiveIntelligenceRow[];
  loading: boolean;
};

const topCategoryPattern = (
  rows: CompanyAICompetitiveIntelligenceRow[],
  field: typeof AICI_COMMUNITY_FIELD.STRONGEST_CATEGORIES | typeof AICI_COMMUNITY_FIELD.WEAKEST_CATEGORIES
) => {
  const counts: Record<string, number> = {};
  rows.forEach((row) => row[field].forEach((category) => (counts[category] = (counts[category] ?? 0) + 1)));
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return top ? { key: top[0], label: categoryLabel(top[0]), count: top[1] } : null;
};

const CommunityRankingsCard = ({ rows, loading }: Props) => {
  const navigate = useNavigate();

  const { total, leadingCount, laggingCount, strongestPattern, weakestPattern, leading, lagging } = useMemo(() => {
    const leadingCount = rows.filter((row) => row.performance === 'leading').length;
    const laggingCount = rows.filter((row) => row.performance === 'lagging').length;

    return {
      total: rows.length,
      leadingCount,
      laggingCount,
      ...selectNotableCommunities(rows),
      strongestPattern: topCategoryPattern(rows, AICI_COMMUNITY_FIELD.STRONGEST_CATEGORIES),
      weakestPattern: topCategoryPattern(rows, AICI_COMMUNITY_FIELD.WEAKEST_CATEGORIES),
    };
  }, [rows]);

  const openCompanyAICI = (queryParam: string) => {
    navigate(`/ai-competitive-intelligence/overall-market-position?${queryParam}`);
  };

  return (
    <Container data-test-id="company-ai-overview-community-rankings-card">
      <Header>
        <TitleGroup>
          <ImageContainer>
            <img src="/icons/stacked-bar-chart-black.svg" width="20" height="20" />
          </ImageContainer>
          <div>
            <Title>How Each Community Ranks Locally</Title>
            <Subtitle>Every community is scored only against its competitors, in its own market.</Subtitle>
          </div>
        </TitleGroup>
        <Actions>
          <AICIButton
            data-test-id="company-ai-overview-ai-ci-button"
            onClick={() => navigate('/ai-competitive-intelligence')}
          >
            <img src="/icons/ai-blob.svg" alt="" width={20} height={20} />
            AI Competitive Intelligence
            <ChevronRight fill="var(--competitor-purple)" width={20} height={20} />
          </AICIButton>
        </Actions>
      </Header>
      {loading ? (
        <LoaderContainer>
          <LoaderRibbed active />
        </LoaderContainer>
      ) : (
        <Body>
          <LeftColumn>
            <StatsBox>
              <StatBlock onClick={() => openCompanyAICI(`performance=${AI_CI_PERFORMANCE.LEADING}`)}>
                <StatBigNumber>{leadingCount}</StatBigNumber>
                <StatUnit>of {total} communities</StatUnit>
                <StatStatus className="leading">are leading their market</StatStatus>
              </StatBlock>
              <StatDivider />
              <StatBlock onClick={() => openCompanyAICI(`performance=${AI_CI_PERFORMANCE.LAGGING}`)}>
                <StatBigNumber>{laggingCount}</StatBigNumber>
                <StatUnit>of {total} communities</StatUnit>
                <StatStatus className="lagging">are lagging in their market</StatStatus>
              </StatBlock>
            </StatsBox>
            <PatternsLabel>NOTABLE CATEGORY PATTERNS</PatternsLabel>
            {strongestPattern || weakestPattern ? (
              <PatternsList>
                {strongestPattern && (
                  <PatternRow onClick={() => openCompanyAICI(`strongestCategory=${strongestPattern.key}`)}>
                    <PatternCategory>{strongestPattern.label}</PatternCategory>
                    <PatternDetail className="leading">
                      Strongest in {strongestPattern.count}/{total} markets
                    </PatternDetail>
                  </PatternRow>
                )}
                {weakestPattern && (
                  <PatternRow onClick={() => openCompanyAICI(`weakestCategory=${weakestPattern.key}`)}>
                    <PatternCategory>{weakestPattern.label}</PatternCategory>
                    <PatternDetail className="lagging">
                      Weakest in {weakestPattern.count}/{total} markets
                    </PatternDetail>
                  </PatternRow>
                )}
              </PatternsList>
            ) : (
              <PatternsEmptyMessage>There are no notable patterns.</PatternsEmptyMessage>
            )}
          </LeftColumn>
          <RightColumn>
            <NotableCommunitiesTable title="Best Positioned" accent="leading" communities={leading} />
            <NotableCommunitiesTable title="Needs Attention" accent="lagging" communities={lagging} />
          </RightColumn>
        </Body>
      )}
    </Container>
  );
};

export default CommunityRankingsCard;
